// Firebase imports
import { 
  collection, 
  addDoc, 
  getDoc,
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db, deleteCourseContentCallable } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { IModuleRepository } from './interfaces';
import { CourseRepository } from './course.repository';
import { LessonRepository, CreateLessonData } from './lesson.repository';
import { QuizRepository, CreateQuizData } from './quiz.repository';

// Module entity interfaces
export interface CreateModuleData {
  title: string;
  description?: string;
  courseId: string;
  certificationId?: string;
  orderIndex: number;
  sequence?: number;
  isPublished: boolean;
  estimatedDurationMinutes?: number;
  prerequisites?: string[];
  learningObjectives?: string[];
  tags?: string[];
  completionCriteria?: string[];
  status?: 'draft' | 'published' | 'archived';
  isRequired?: boolean;
}

export interface UpdateModuleData {
  title?: string;
  description?: string;
  orderIndex?: number;
  isPublished?: boolean;
  estimatedDurationMinutes?: number;
  prerequisites?: string[];
  learningObjectives?: string[];
  tags?: string[];
  updatedAt?: Timestamp;
}

export interface ModuleFilter {
  courseId?: string;
  certificationId?: string;
  isPublished?: boolean;
  tag?: string;
}

export interface GetModuleData {
  id: string;
  title: string;
  description?: string;
  courseId: string;
  orderIndex: number;
  isPublished: boolean;
  estimatedDurationMinutes?: number;
  prerequisites?: string[];
  learningObjectives?: string[];
  tags?: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class ModuleRepository implements IModuleRepository {
  constructor(private db: any) {
    console.log('[ModuleRepository] Constructor - Injected Firebase Admin DB instance:', !!this.db);
  }

  private collectionName = 'modules';

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  // Create module
  async createModule(moduleData: CreateModuleData): Promise<GetModuleData> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...moduleData,
        // Published unless told otherwise. Whether learners can see it is
        // the course's own Published switch; the editors have no per-module or
        // per-lesson switch, so a 'false' default made every authored module
        // and lesson invisible to course completion -- 100% done, no
        // certificate, ever.
        isPublished: moduleData.isPublished ?? true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'MODULE_CREATE_MISSING_DOCUMENT',
          devMessage: 'Module document was missing after create',
          context: { courseId: moduleData.courseId, title: moduleData.title, orderIndex: moduleData.orderIndex },
        });
      }

      return {
        id: docRef.id,
        ...docSnap.data(),
      } as GetModuleData;
    } catch (error) {
      console.error('Error creating module:', error);
      throw this.toRepositoryError(error, 'MODULE_CREATE_FAILED', 'Failed to create module', {
        courseId: moduleData.courseId,
        title: moduleData.title,
        orderIndex: moduleData.orderIndex,
      });
    }
  }

  // Get module by ID
  async getModuleById(moduleId: string): Promise<GetModuleData | null> {
    try {
      const docRef = doc(db, this.collectionName, moduleId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetModuleData;
    } catch (error) {
      console.error('Error getting module by ID:', error);
      throw this.toRepositoryError(error, 'MODULE_FETCH_FAILED', `Failed to get module ${moduleId}`, { moduleId });
    }
  }

  // Get modules with filters
  async getModules(filter: ModuleFilter = {}, limitCount?: number): Promise<GetModuleData[]> {
    console.log('[ModuleRepository.getModules] START - Filter:', filter, 'Limit:', limitCount);
    try {
      console.log('[ModuleRepository.getModules] Building query for collection:', this.collectionName);
      
      // First test if collection exists and has any documents
      const testQuery = query(collection(db, this.collectionName), limit(10));
      const testSnapshot = await getDocs(testQuery);
      console.log('[ModuleRepository.getModules] Test query - Total documents in', this.collectionName, 'collection:', testSnapshot.size);
      
      if (testSnapshot.size > 0) {
        console.log('[ModuleRepository.getModules] Sample document IDs:', testSnapshot.docs.slice(0, 3).map(doc => doc.id));
        testSnapshot.docs.slice(0, 2).forEach((doc, index) => {
          console.log(`[ModuleRepository.getModules] Sample document ${index}:`, doc.data());
        });
      }
      
      const constraints = [];

      // Build query with filters
      if (filter.courseId) {
        console.log('[ModuleRepository.getModules] Adding courseId filter:', filter.courseId);
        constraints.push(where('courseId', '==', filter.courseId));
      }
      if (filter.certificationId) {
        console.log('[ModuleRepository.getModules] Adding certificationId filter:', filter.certificationId);
        constraints.push(where('certificationId', '==', filter.certificationId));
      }
      if (filter.isPublished !== undefined) {
        console.log('[ModuleRepository.getModules] Adding isPublished filter:', filter.isPublished);
        constraints.push(where('isPublished', '==', filter.isPublished));
      }
      if (filter.tag) {
        console.log('[ModuleRepository.getModules] Adding tag filter:', filter.tag);
        constraints.push(where('tags', 'array-contains', filter.tag));
      }

      // Add limit
      if (limitCount) {
        console.log('[ModuleRepository.getModules] Adding limit:', limitCount);
        constraints.push(limit(limitCount));
      }

      // Build the final query
      let q;
      if (constraints.length > 0) {
        q = query(collection(db, this.collectionName), ...constraints);
        console.log('[ModuleRepository.getModules] Built query with', constraints.length, 'constraints');
      } else {
        q = collection(db, this.collectionName);
        console.log('[ModuleRepository.getModules] Built simple collection query (no constraints)');
      }

      console.log('[ModuleRepository.getModules] Executing query...');
      const querySnapshot = await getDocs(q);
      console.log('[ModuleRepository.getModules] Query executed, docs count:', querySnapshot.size);
      
      const modules: GetModuleData[] = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        console.log('[ModuleRepository.getModules] Processing doc:', doc.id, 'with data keys:', Object.keys(docData));
        modules.push({
          id: doc.id,
          ...docData,
        } as GetModuleData);
      });

      // Sort by orderIndex in memory to avoid composite index requirement
      modules.sort((a, b) => {
        const seq = (m: GetModuleData) =>
          m.orderIndex ?? (m as { sequence?: number }).sequence ?? 0;
        return seq(a) - seq(b);
      });

      console.log('[ModuleRepository.getModules] SUCCESS - Retrieved', modules.length, 'modules');
      return modules;
    } catch (error) {
      console.error('[ModuleRepository.getModules] ERROR:', error);
      throw this.toRepositoryError(error, 'MODULES_FETCH_FAILED', 'Failed to get modules', { filter, limitCount });
    }
  }

  // Update module
  async updateModule(moduleId: string, updateData: UpdateModuleData): Promise<GetModuleData> {
    try {
      const docRef = doc(db, this.collectionName, moduleId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'MODULE_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Module ${moduleId} not found after update`,
          userMessage: 'Module not found.',
          level: 'warning',
          context: { moduleId, updateData },
        });
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetModuleData;
    } catch (error) {
      console.error('Error updating module:', error);
      throw this.toRepositoryError(error, 'MODULE_UPDATE_FAILED', `Failed to update module ${moduleId}`, { moduleId, updateData });
    }
  }

  // Delete module
  async deleteModule(moduleId: string): Promise<void> {
    try {
      // Deleting only this document left everything under it -- modules,
      // lessons, quizzes, questions, material records -- and every uploaded
      // file behind. The server deletes the whole tree and its files, and
      // checks the caller owns the course (the rules only let admins delete).
      await deleteCourseContentCallable({ scope: 'module', id: moduleId });
    } catch (error) {
      console.error('Error deleting module:', error);
      throw this.toRepositoryError(error, 'MODULE_DELETE_FAILED', `Failed to delete module ${moduleId}`, { moduleId });
    }
  }

  // Get module by title
  async getModuleByTitle(title: string): Promise<GetModuleData | null> {
    try {
      const q = query(collection(db, this.collectionName), where('title', '==', title));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      } as GetModuleData;
    } catch (error) {
      console.error('Error getting module by title:', error);
      throw this.toRepositoryError(error, 'MODULE_BY_TITLE_FETCH_FAILED', `Failed to get module by title ${title}`, { title });
    }
  }

  // Get modules by course
  async getModulesByCourse(courseId: string): Promise<GetModuleData[]> {
    try {
      const primary = await this.getModules({ courseId });
      if (primary.length > 0) {
        return primary;
      }
      return this.getModules({ certificationId: courseId });
    } catch (error) {
      console.error('Error getting modules by course:', error);
      throw this.toRepositoryError(error, 'MODULES_BY_COURSE_FETCH_FAILED', `Failed to get modules for course ${courseId}`, { courseId });
    }
  }

  // Get modules by tag
  async getModulesByTag(tagId: string, limitCount?: number): Promise<GetModuleData[]> {
    try {
      const filter: ModuleFilter = { tag: tagId };
      return this.getModules(filter, limitCount);
    } catch (error) {
      console.error('Error getting modules by tag:', error);
      throw this.toRepositoryError(error, 'MODULES_BY_TAG_FETCH_FAILED', `Failed to get modules for tag ${tagId}`, { tagId, limitCount });
    }
  }

  // Get modules by owner (course instructor)
  async getModulesByOwner(ownerId: string, limitCount?: number): Promise<GetModuleData[]> {
    try {
      // First get courses by instructor, then get modules for those courses
      const courseRepo = new CourseRepository();
      const courses = await courseRepo.getCoursesByInstructor(ownerId);
      const courseIds = courses.map(course => course.id);

      if (courseIds.length === 0) {
        return [];
      }

      // Get modules for all courses by this instructor
      const modules: GetModuleData[] = [];
      for (const courseId of courseIds) {
        const courseModules = await this.getModulesByCourse(courseId);
        modules.push(...courseModules);
      }

      return modules.slice(0, limitCount || modules.length);
    } catch (error) {
      console.error('Error getting modules by owner:', error);
      throw this.toRepositoryError(error, 'MODULES_BY_OWNER_FETCH_FAILED', `Failed to get modules for owner ${ownerId}`, { ownerId, limitCount });
    }
  }

  // Get all modules (admin only) or owner's modules
  async getModulesForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetModuleData[]> {
    try {
      if (isAdmin) {
        // Admin sees all modules
        return this.getModules({}, limitCount);
      } else {
        // Regular user sees only their own modules
        return this.getModulesByOwner(userId, limitCount);
      }
    } catch (error) {
      console.error('Error getting modules for user:', error);
      throw this.toRepositoryError(error, 'MODULES_FOR_USER_FETCH_FAILED', `Failed to get modules for user ${userId}`, { userId, isAdmin, limitCount });
    }
  }

  // Get all modules (admin only)
  async getAllModules(limitCount?: number): Promise<GetModuleData[]> {
    try {
      return this.getModules({}, limitCount);
    } catch (error) {
      console.error('Error getting all modules:', error);
      throw this.toRepositoryError(error, 'MODULES_ALL_FETCH_FAILED', 'Failed to get all modules', { limitCount });
    }
  }

  // Create lesson for module
  async createModuleLesson(moduleId: string, lessonData: Omit<CreateLessonData, 'moduleId'>): Promise<any> {
    try {
      const lessonRepo = new LessonRepository();
      return await lessonRepo.createLesson({
        ...lessonData,
        moduleId: moduleId,
      });
    } catch (error) {
      console.error('Error creating module lesson:', error);
      throw this.toRepositoryError(error, 'MODULE_LESSON_CREATE_FAILED', `Failed to create lesson for module ${moduleId}`, { moduleId, lessonData });
    }
  }

  // Create quiz for module
  async createModuleQuiz(moduleId: string, quizData: Omit<CreateQuizData, 'lessonId'>): Promise<any> {
    try {
      const quizRepo = new QuizRepository();
      return await quizRepo.createQuiz({
        ...quizData,
        lessonId: null, // Module-level quiz
      });
    } catch (error) {
      console.error('Error creating module quiz:', error);
      throw this.toRepositoryError(error, 'MODULE_QUIZ_CREATE_FAILED', `Failed to create quiz for module ${moduleId}`, { moduleId, quizData });
    }
  }

  // Get lessons in module
  async getModuleLessons(moduleId: string): Promise<any[]> {
    try {
      const lessonRepo = new LessonRepository();
      return await lessonRepo.getLessonsByModule(moduleId);
    } catch (error) {
      console.error('Error getting module lessons:', error);
      throw this.toRepositoryError(error, 'MODULE_LESSONS_FETCH_FAILED', `Failed to get lessons for module ${moduleId}`, { moduleId });
    }
  }

  // Get quizzes in module
  async getModuleQuizzes(moduleId: string): Promise<any[]> {
    try {
      const quizRepo = new QuizRepository();
      return await quizRepo.getQuizzesByLesson(null); // Module-level quizzes
    } catch (error) {
      console.error('Error getting module quizzes:', error);
      throw this.toRepositoryError(error, 'MODULE_QUIZZES_FETCH_FAILED', `Failed to get quizzes for module ${moduleId}`, { moduleId });
    }
  }

  // Get module content (lessons + quizzes)
  async getModuleContent(moduleId: string): Promise<{
    module: GetModuleData;
    lessons: any[];
    quizzes: any[];
  }> {
    try {
      const [module, lessons, quizzes] = await Promise.all([
        this.getModuleById(moduleId),
        this.getModuleLessons(moduleId),
        this.getModuleQuizzes(moduleId)
      ]);

      if (!module) {
        throw new DRXError({
          code: 'MODULE_NOT_FOUND',
          devMessage: `Module ${moduleId} not found`,
          userMessage: 'Module not found.',
          level: 'warning',
          context: { moduleId },
        });
      }

      return {
        module,
        lessons,
        quizzes
      };
    } catch (error) {
      console.error('Error getting module content:', error);
      throw this.toRepositoryError(error, 'MODULE_CONTENT_FETCH_FAILED', `Failed to get content for module ${moduleId}`, { moduleId });
    }
  }

  // Get published modules
  async getPublishedModules(limitCount?: number): Promise<GetModuleData[]> {
    try {
      const filter: ModuleFilter = { isPublished: true };
      return this.getModules(filter, limitCount);
    } catch (error) {
      console.error('Error getting published modules:', error);
      throw this.toRepositoryError(error, 'MODULES_PUBLISHED_FETCH_FAILED', 'Failed to get published modules', { limitCount });
    }
  }

  // Get published modules by course
  async getPublishedModulesByCourse(courseId: string): Promise<GetModuleData[]> {
    try {
      const filter: ModuleFilter = {
        courseId: courseId,
        isPublished: true
      };
      return this.getModules(filter);
    } catch (error) {
      console.error('Error getting published modules by course:', error);
      throw this.toRepositoryError(error, 'MODULES_PUBLISHED_BY_COURSE_FETCH_FAILED', `Failed to get published modules for course ${courseId}`, { courseId });
    }
  }

  // Reorder modules in a course
  async reorderModules(courseId: string, moduleOrders: { moduleId: string; orderIndex: number }[]): Promise<void> {
    try {
      const updatePromises = moduleOrders.map(({ moduleId, orderIndex }) =>
        updateDoc(doc(db, this.collectionName, moduleId), {
          orderIndex,
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error reordering modules:', error);
      throw this.toRepositoryError(error, 'MODULES_REORDER_FAILED', `Failed to reorder modules for course ${courseId}`, {
        courseId,
        moduleOrders,
      });
    }
  }

  // Get next module in course
  async getNextModule(currentModuleId: string): Promise<GetModuleData | null> {
    try {
      const currentModule = await this.getModuleById(currentModuleId);
      if (!currentModule) {
        return null;
      }

      const courseModules = await this.getModulesByCourse(currentModule.courseId);
      const currentIndex = courseModules.findIndex(module => module.id === currentModuleId);

      if (currentIndex === -1 || currentIndex === courseModules.length - 1) {
        return null;
      }

      return courseModules[currentIndex + 1];
    } catch (error) {
      console.error('Error getting next module:', error);
      throw this.toRepositoryError(error, 'MODULE_NEXT_FETCH_FAILED', `Failed to get next module from ${currentModuleId}`, { currentModuleId });
    }
  }

  // Get previous module in course
  async getPreviousModule(currentModuleId: string): Promise<GetModuleData | null> {
    try {
      const currentModule = await this.getModuleById(currentModuleId);
      if (!currentModule) {
        return null;
      }

      const courseModules = await this.getModulesByCourse(currentModule.courseId);
      const currentIndex = courseModules.findIndex(module => module.id === currentModuleId);

      if (currentIndex <= 0) {
        return null;
      }

      return courseModules[currentIndex - 1];
    } catch (error) {
      console.error('Error getting previous module:', error);
      throw this.toRepositoryError(error, 'MODULE_PREVIOUS_FETCH_FAILED', `Failed to get previous module from ${currentModuleId}`, { currentModuleId });
    }
  }

  // Calculate course progress based on modules
  async calculateCourseProgress(courseId: string): Promise<{
    totalModules: number;
    completedModules: number;
    progressPercentage: number;
  }> {
    try {
      const modules = await this.getModulesByCourse(courseId);
      const publishedModules = modules.filter(module => module.isPublished);

      return {
        totalModules: publishedModules.length,
        completedModules: publishedModules.length, // This would need user progress tracking
        progressPercentage: 100, // This would need user progress tracking
      };
    } catch (error) {
      console.error('Error calculating course progress:', error);
      throw this.toRepositoryError(error, 'MODULES_COURSE_PROGRESS_FAILED', `Failed to calculate module-based course progress for ${courseId}`, { courseId });
    }
  }
}
